import { useDisclosure, useMediaQuery } from "@chakra-ui/react"
import {
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useHistory } from "react-router-dom"
import {
  CohortSyncPathCohortFragment,
  PathItemFragment,
  ResourceFragment,
  usePathQuery
} from "@pathwright/graphql-client/src/generated"
import { RESOURCE_TYPE_COLLECTION } from "../../resource/constants"
import { PATH_MODE_LABELS } from "../constants"
import { useScrollContext } from "@pathwright/ui/src/components/scroll/ScrollView"
import { flattenEdges } from "@pathwright/utils"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { LinkProps } from "@pathwright/ux/src/components/link/Link"
import { MouseEvent } from "react"
import { useWindowSize } from "react-use"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { trackEvent } from "../../plausible/events"
import { getGroupUrl, getStepUrl } from "../../utils/urls"
import { useResourceContext } from "../ResourceContext"
import { PathQueryPath } from "../types"
import { PathNavigatorPathFilters } from "./PathNavigatorFilterMenu"
import { flattenPathBlocks, useBlocksContent } from "./blocks"
import { useNavigatorSearchResults } from "./search"
import {
  HighlightStyle,
  NavigatorItem,
  NavigatorPlausibleEvent,
  SearchableBlockType,
  SearchablePathItem
} from "./types"
import { getPathQueryVariables } from "./utils"

type PathNavigatorContext = {
  isOpen: boolean
  onOpen: (highlightItem?: boolean) => void
  onClose: (clearSearch?: boolean) => void
  items: NavigatorItem[]
  pathMode: keyof typeof PATH_MODE_LABELS
  search: string
  setSearch: (search: string) => void
  path: PathQueryPath
  cohort: CohortSyncPathCohortFragment
  resource: ResourceFragment
  setFocusedItemId: Dispatch<SetStateAction<number | string | undefined>>
  setHighlightStyle: Dispatch<SetStateAction<HighlightStyle>>
  highlightStyle: HighlightStyle
  searchIsLoading: boolean
  resultsAreLoading: boolean
  isMobile: boolean
  isPhone: boolean
  hasHover: boolean
  searchInputRef: MutableRefObject<HTMLInputElement | null>
  handleNavigateToItem: (
    item: PathItemFragment,
    event?: MouseEvent | KeyboardEvent,
    itemUrl?: string
  ) => void
  handleLogEvent: (eventName: NavigatorPlausibleEvent) => void
  inlineMode: boolean
  setIsInlineMode: Dispatch<SetStateAction<boolean>>
  focusedItemId?: number | string
  currentItem?: PathItemFragment
  isCollection?: boolean
  navigatorPathFilters: PathNavigatorPathFilters
  setNavigatorPathFilters: Dispatch<SetStateAction<PathNavigatorPathFilters>>
  certificateLink?: string
  getResultURL?: (item: PathItemFragment, blockSuffix?: string) => string | null
  getResultProps?: (item: PathItemFragment) => LinkProps
  displayFilterMenu?: boolean
}

export type HoverRect = {
  top: number
  height: number
  width: number
} | null

export const PathNavigatorContext = createContext<PathNavigatorContext>(
  null as any
)

export const usePathNavigator = () =>
  useContext<PathNavigatorContext>(PathNavigatorContext)

type PathNavigatorProviderProps = {
  children: ReactNode
  cohort: CohortSyncPathCohortFragment
  resource: ResourceFragment
  pathMode: keyof typeof PATH_MODE_LABELS
  currentItem?: PathItemFragment
  toggleExpandLesson?: (lessonId: number) => void
  getIsLessonExpanded?: (lessonId: number) => boolean
  certificateLink?: string
  getResultURL?: (item: PathItemFragment) => string | null
  getResultProps?: (item: PathItemFragment) => LinkProps
  displayFilterMenu?: boolean
}

// Possibly not the best check. Checking for App.getStore("step").getState().step isn't reliable,
// since it's not unset when returning from Step to Path.
const getIsStepView = (pathName: string) => {
  return pathName.includes("/step/")
}

// Use an empty array as default, mainly benefiting from the constant reference
// so that dependencies in useEffect and useMemo don't trigger unnecessarily.
// NOTE: the resulting type is any (would need to update typing in useBlocksContent).
const defaultBlocksContentList = [] as ReturnType<
  typeof useBlocksContent
>["data"]["contentList"]

const PathNavigatorProvider = ({
  children,
  cohort,
  resource,
  pathMode,
  currentItem,
  toggleExpandLesson,
  getIsLessonExpanded,
  certificateLink,
  getResultURL,
  getResultProps,
  displayFilterMenu = false
}: PathNavigatorProviderProps) => {
  const { open, onOpen, onClose } = useDisclosure()
  // Optionally load the full Path with all items (including archived ones),
  const [navigatorPathFilters, setNavigatorPathFilters] =
    useState<PathNavigatorPathFilters>(null)

  // Use the ResourceContext for scoping the PathQuery variables, where the pathId
  // could be null or number depending on if we're scoping to a learner's path or a Cohort path.
  const resourceContext = useResourceContext()
  const pathQuery = usePathQuery({
    variables: getPathQueryVariables({
      pathId: resourceContext.pathId,
      userId: resourceContext.userId,
      cohortId: resourceContext.cohortId,
      navigatorPathFilters
    }),
    skip: !open
  })
  const path = (pathQuery.data || pathQuery.previousData)?.path
  const pathItemsLoading = pathQuery.loading

  const { smoothScrollTo, scrollNode } = useScrollContext()

  const { me, school } = usePathwrightContext()

  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])
  const isPhone = useMemo(() => width < 640, [width])
  const [hasHover] = useMediaQuery(["(hover: hover)"])

  const searchInputRef = useRef<HTMLInputElement>(null)

  // When the URL changes (ex: from Step to Path), update the isStepView state
  const history = useHistory()
  const [isStepView, setIsStepView] = useState<boolean>(
    getIsStepView(history?.location?.pathname)
  )
  history.listen((location) => {
    setIsStepView(getIsStepView(location?.pathname))
  })

  const [search, setSearch] = useState<string>("")
  const [searchIsLoading, setSearchIsLoading] = useState<boolean>(false)
  const [focusedItemId, setFocusedItemId] = useState<
    number | string | undefined
  >(undefined)
  // Whether the inline version of the Navigator is open (for Step views)
  const [inlineMode, setIsInlineMode] = useState<boolean>(false)
  const [shouldHighlightItem, setShouldHighlightItem] = useState<boolean>(false)
  const [highlightStyle, setHighlightStyle] = useState({
    transform: "translateY(0px)",
    opacity: 0
  })

  const pathItems = useMemo(
    () =>
      flattenEdges(path?.items)?.filter(
        (item: PathItemFragment) => item.type !== "divider"
      ) || [],
    [path?.items]
  )

  // Step Blocks content uses Step.ids as contextKeys to resolve Block content
  const contextKeys = useMemo(
    () =>
      (pathItems || [])
        .filter((item: PathItemFragment) => item.type === "step")
        // Use the full contextKey for the Step (resource/cohort/step)
        .map(
          (item: PathItemFragment) =>
            `/${resource.id}/${cohort!.id}/${item.source_id}`
        ),
    [pathItems]
  )

  // Returns an array of Blocks Content documents
  const {
    loading: blocksLoading,
    data: { contentList = defaultBlocksContentList } = {}
  } = useBlocksContent({
    pathMode,
    contextKeys,
    skip: !search || !open
  })

  // Flattens each Content.blocks array into a single array of Blocks
  // Each block has its Content's contextKey to identify the Step it belongs to
  const flattenedBlocks = useMemo(
    () => flattenPathBlocks(contentList),
    [contentList]
  )

  // We treat both Path Items and Blocks as Indexable items
  // We return original items from the search index with searchResult metadata
  // This allows us to highlight the matched text in the Navigator
  const renderedItems = useNavigatorSearchResults(
    pathItems,
    flattenedBlocks,
    search
  ) as (SearchablePathItem | SearchableBlockType)[]

  const handleLogEvent = (eventName: NavigatorPlausibleEvent) => {
    const role = me?.membership?.role || 0
    let roleLabel = PATH_MODE_LABELS[role as keyof typeof PATH_MODE_LABELS]
    if (role === 0 && me?.permissions?.has_pathwright_staff_access) {
      roleLabel = "superuser"
    }
    trackEvent({
      domain: "pathwright-platform.com",
      event: eventName,
      props: {
        spaceId: school?.id,
        userId: me?.id,
        userRole: roleLabel
      }
      // trackLocalhost: false
    })
  }

  // If the Navigator is displayed in the context of one of the items that it can navigate to,
  // highlightItem will be true
  const handleOpen = (highlightItem?: boolean) => {
    // Default navigator to non-inline mode when opening, allowing the caller
    // to set inlineMode as needed after opening.
    setIsInlineMode(false)
    onOpen()
    if (highlightItem) setShouldHighlightItem(true)
    // handleLogEvent("Navigator: launch")
  }

  const handleClose = (clearSearch = true) => {
    onClose()
    if (clearSearch) {
      setSearch("")
    }
    setFocusedItemId(undefined)
    // handleLogEvent("Navigator: close")
  }

  const handleSetSearch = (search: string) => {
    setSearchIsLoading(true)
    setSearch(search)
    setHighlightStyle({ transform: "translateY(0px)", opacity: 0 })
    setFocusedItemId(undefined)
  }

  useEffect(() => {
    setTimeout(() => {
      setSearchIsLoading(false)
    }, 300)
  }, [renderedItems])

  const loading = pathItemsLoading || blocksLoading
  useEffect(() => {
    setSearchIsLoading(!!loading)
  }, [loading])

  const handleScrollToLesson = (lesson: PathItemFragment) => {
    if (!lesson || lesson.type !== "lesson") return

    const lessonID = lesson.source_id
    const lessonElement = document.getElementById(`lesson-${lessonID}`)

    if (lessonElement) {
      const lessonRect = lessonElement.getBoundingClientRect()
      // scrollNode is the parent scroll element
      const scrollNodeRect = scrollNode.getBoundingClientRect()

      // Calculate the position relative to the scrollNode
      const scrollOffset =
        lessonRect.top - scrollNodeRect.top + scrollNode.scrollTop - 150 // A small offset

      smoothScrollTo(scrollOffset, 0, 500)
    }

    // focus the element in the Navigator
    setFocusedItemId(lesson.id)

    // Expand lesson if it's not expanded
    const isExpanded = getIsLessonExpanded
      ? getIsLessonExpanded(lessonID)
      : false
    if (!isExpanded && toggleExpandLesson) {
      toggleExpandLesson(lessonID)
    }
  }

  const handleNavigateToItem = (
    item: PathItemFragment,
    event?: MouseEvent | KeyboardEvent,
    itemUrl?: string
  ) => {
    if (!item) return

    if (getResultURL) {
      const url = getResultURL(item)
      if (url) {
        history.push(url)
        onClose()
      }
      return
    }

    // handleLogEvent("Navigator: visit item")
    if (search) handleLogEvent("Navigator: visit item after search")

    if (item.type === "lesson") {
      // Scroll to the itemURL (hash) instead of navigating to the standalone lesson view
      const url = getGroupUrl(resource.slug, cohort!.id)
      // If this is a lesson and we're opening the Navigator in the Step view, don't following the link.
      if (currentItem) {
        history.push(url)
      }
      setTimeout(
        () => {
          handleScrollToLesson(item)
        },
        currentItem ? 300 : 0
      )

      if (isMobile) onClose()
    } else {
      const url =
        itemUrl || getStepUrl(resource.slug, cohort!.id, item.source_id)

      history.push(url)

      onClose()
    }
  }

  // Whether to highlight the item when opening the Navigator via keyboard shortcut.
  const highlightItem = isStepView && !!currentItem

  const handleKeyControl = useCallback(() => {
    if (open) {
      handleClose()
    } else {
      handleOpen(highlightItem)
    }
  }, [handleClose, handleOpen, open, highlightItem])

  // open with command or ctrl plus K
  useKeyControl("Meta+K", handleKeyControl)
  // For some reason, Chakra is not detecting the Escape key to close the Drawer
  // (likely because not passing the `closeOnEscape` prop` to Drawer, but the Popover
  // doesn't seem to handle that prop as expected, so let's just keep using this handler).
  useKeyControl("Escape", () => handleClose())

  // find item by currentItemId
  // scroll to the item smoothly
  useEffect(() => {
    // currentItem is defined when we're in a Step view
    if (!currentItem) return

    const index = renderedItems.findIndex(
      (item: SearchablePathItem | SearchableBlockType) =>
        item.id === currentItem.id
    )

    if (index === -1) return

    setFocusedItemId(currentItem.id)

    if (!shouldHighlightItem) return

    setShouldHighlightItem(false)

    setTimeout(() => {
      const currentItemIndex = renderedItems.findIndex(
        (item: SearchablePathItem | SearchableBlockType) =>
          item.id === currentItem.id
      )
      const item3before = renderedItems[currentItemIndex - 3]
      const item2before = renderedItems[currentItemIndex - 2]
      const item1before = renderedItems[currentItemIndex - 1]

      const targetItem =
        item3before || item2before || item1before || currentItem

      const itemRef = document.getElementById(`navigator-item-${targetItem.id}`)
      if (itemRef) {
        itemRef.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }, 300)
  }, [currentItem, shouldHighlightItem, renderedItems, open])

  const isCollection = useMemo(
    () => resource?.resource_type === RESOURCE_TYPE_COLLECTION,
    [resource]
  )

  if (!resource || !cohort) return null

  return (
    <>
      <PathNavigatorContext.Provider
        value={{
          isOpen: open,
          onOpen: handleOpen,
          onClose: handleClose,
          items: renderedItems,
          search,
          setSearch: handleSetSearch,
          path,
          cohort,
          resource,
          focusedItemId,
          setFocusedItemId,
          currentItem,
          pathMode,
          highlightStyle,
          setHighlightStyle,
          isCollection,
          searchIsLoading,
          resultsAreLoading: !!loading,
          isMobile,
          isPhone,
          hasHover,
          searchInputRef,
          handleNavigateToItem,
          handleLogEvent,
          inlineMode,
          setIsInlineMode,
          navigatorPathFilters,
          setNavigatorPathFilters,
          certificateLink,
          getResultURL,
          getResultProps,
          displayFilterMenu
        }}
      >
        {children}
      </PathNavigatorContext.Provider>
    </>
  )
}

export default PathNavigatorProvider
